/**
 * Экран одного кейса.
 *
 * Открытие вынесено из витрины отдельным экраном: пока игрок выбирает, ему не
 * нужны ни лента, ни кнопка списания лапок, а когда выбрал — не нужен каталог,
 * мешающий случайно нажать соседний кейс во время прокрутки.
 */

import { useCallback, useEffect, useState } from "react";

import { formatPaws, hasPremiumPerks, maxMultiPlay, type CaseDto, type CaseOpeningDto } from "@koshkodrop/shared";

import { ApiError, api } from "../../lib/api";
import { haptic } from "../../lib/telegram";
import { useSession } from "../../store/session";

import { CaseContents } from "./CaseContents";
import { CaseReel } from "./CaseReel";
import { PrizeOverlay } from "./PrizeOverlay";
import { RARITY_COLOR } from "./rarity";

interface Props {
  item: CaseDto;
}

export function CaseOpenView({ item }: Props) {
  const { balance, setBalance, user } = useSession();
  const [opening, setOpening] = useState<CaseOpeningDto | null>(null);
  const [packOpenings, setPackOpenings] = useState<CaseOpeningDto[]>([]);
  const [count, setCount] = useState(1);
  const [history, setHistory] = useState<CaseOpeningDto[]>([]);
  const [spinKey, setSpinKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .listCaseOpenings(30)
      .then((data) => setHistory(data.openings.filter((row) => row.caseSlug === item.slug)))
      .catch(() => undefined);
  }, [item.slug]);

  const price = BigInt(item.price);
  const maxCount = maxMultiPlay(user?.group ?? "user");
  const premium = hasPremiumPerks(user?.group ?? "user");
  const pack = Math.min(count, maxCount);
  const totalPrice = price * BigInt(pack);
  const affordable = balance >= totalPrice && item.available;

  const open = async () => {
    if (busy || spinning) return;
    if (!item.available) {
      haptic.error();
      setError("Кейс закрыт: казна пока не покрывает его джекпот");
      return;
    }
    if (!affordable) {
      haptic.error();
      setError("Не хватает лапок на этот кейс");
      return;
    }

    setBusy(true);
    setError(null);
    setRevealed(false);
    try {
      const result = await api.openCase(item.slug, pack);
      const openings = result.openings ?? [result.opening];
      const last = openings[openings.length - 1] ?? result.opening;
      setOpening(last);
      setPackOpenings(openings);
      setBalance(BigInt(result.balance));
      setSpinKey((key) => key + 1);
      setSpinning(true);
      haptic.tap("heavy");
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось открыть кейс");
    } finally {
      setBusy(false);
    }
  };

  const finishSpin = useCallback(() => {
    setSpinning(false);
    setRevealed(true);
    haptic.success();
    setHistory((rows) => {
      const incoming = packOpenings.length > 0 ? packOpenings : opening ? [opening] : [];
      const merged = [
        ...incoming.filter((row) => !rows.some((item) => item.id === row.id)),
        ...rows,
      ];
      return merged.slice(0, 12);
    });
  }, [opening, packOpenings]);

  const best = item.items.reduce(
    (top, entry) => (top && BigInt(top.value) >= BigInt(entry.value) ? top : entry),
    item.items[0],
  );

  return (
    <div className="space-y-4">
      <div
        className="rounded-[28px] border border-white/8 p-3"
        style={{
          background: `linear-gradient(180deg, color-mix(in oklab, ${item.accent} 16%, #12102e), #0a0920)`,
        }}
      >
        <div className="mb-3 flex items-center gap-3 px-1">
          <span
            className="text-[38px] leading-none"
            style={{ filter: `drop-shadow(0 6px 16px color-mix(in oklab, ${item.accent} 60%, transparent))` }}
          >
            {item.emoji}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-bold uppercase tracking-wide text-white/30">Цена</p>
            <p className="tabular text-sm font-extrabold" style={{ color: item.accent }}>
              {formatPaws(price)} 🐾
            </p>
          </div>
          {best && (
            <div className="shrink-0 text-right">
              <p className="text-[9px] font-bold uppercase tracking-wide text-white/30">Топ приз</p>
              <p
                className="tabular text-xs font-extrabold"
                style={{ color: RARITY_COLOR[best.rarity] }}
              >
                {best.emoji} {formatPaws(BigInt(best.value))}
              </p>
            </div>
          )}
        </div>

        <CaseReel
          items={item.items}
          winnerId={opening?.item.id ?? null}
          spinKey={spinKey}
          running={spinning}
          onDone={finishSpin}
        />
      </div>

      {error && (
        <p className="rounded-2xl bg-lose-500/15 px-4 py-2.5 text-center text-sm font-semibold text-lose-400">
          {error}
        </p>
      )}

      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-white/35">
          {premium ? "Сколько открыть" : "Пачка кейсов · Premium"}
        </p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((value) => {
            const locked = value > maxCount;
            return (
              <button
                key={value}
                type="button"
                onClick={() => {
                  if (locked) {
                    haptic.error();
                    setError("Открыть пачкой — для Premium");
                    return;
                  }
                  haptic.select();
                  setCount(value);
                }}
                className={`flex-1 rounded-xl py-2 text-sm font-bold ${
                  pack === value && !locked
                    ? "bg-paws-500 text-night-950"
                    : locked
                      ? "bg-night-850 text-white/25"
                      : "bg-night-850 text-white/55"
                }`}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => void open()}
        disabled={busy || spinning || !affordable}
        className="w-full rounded-2xl py-4 text-base font-extrabold text-night-950 transition-transform active:scale-[0.98] disabled:opacity-35"
        style={{
          background: item.accent,
          boxShadow: `0 12px 30px color-mix(in oklab, ${item.accent} 36%, transparent)`,
        }}
      >
        {spinning
          ? "Крутим…"
          : !item.available
            ? "Кейс временно закрыт"
            : affordable
              ? pack > 1
                ? `Открыть ×${pack} · ${formatPaws(totalPrice)} 🐾`
                : `Открыть за ${formatPaws(price)} 🐾`
              : "Недостаточно лапок"}
      </button>

      {history.length > 0 && <RecentDrops openings={history} />}

      <CaseContents items={item.items} />

      {revealed && opening && (
        <PrizeOverlay
          opening={opening}
          extras={packOpenings.length > 1 ? packOpenings : []}
          canRepeat={affordable}
          onRepeat={() => {
            setRevealed(false);
            void open();
          }}
          onClose={() => setRevealed(false)}
        />
      )}
    </div>
  );
}

function RecentDrops({ openings }: { openings: CaseOpeningDto[] }) {
  return (
    <section className="space-y-1.5">
      <h3 className="px-1 text-xs font-extrabold uppercase tracking-wide text-white/40">
        Ваши дропы из этого кейса
      </h3>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {openings.map((row) => {
          const color = RARITY_COLOR[row.item.rarity];
          return (
            <div
              key={row.id}
              className="flex w-[64px] shrink-0 flex-col items-center rounded-2xl border py-2"
              style={{
                borderColor: `color-mix(in oklab, ${color} 35%, transparent)`,
                background: `color-mix(in oklab, ${color} 10%, #0e0c26)`,
              }}
            >
              <span className="text-xl leading-none">{row.item.emoji}</span>
              <span className="tabular mt-1 text-[9px] font-bold" style={{ color }}>
                {formatPaws(BigInt(row.payout))}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
